import apiClient from '../client';
import type { InfluencerSchema } from '@appTypes';

// Influencers API
export type { InfluencerSchema } from '@appTypes';

/**
 * Fetch all influencers (non-paginated)
 */
export async function fetchInfluencers(): Promise<{ [key: string]: InfluencerSchema } | undefined> {
  const response: any = await apiClient.get('/referral/influencers', {
    params: { paginated: false },
  });
  return response.data;
}

/**
 * Fetch paginated influencers
 */
export async function fetchPaginatedInfluencers(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: InfluencerSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/referral/influencers', {
    params: { paginated: true, lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

/**
 * Get a specific influencer by ID
 */
export async function getInfluencerById(params: { influencerId: string }): Promise<InfluencerSchema | undefined> {
  const response: any = await apiClient.get(`/referral/influencers/${params.influencerId}`);
  return response.data;
}

/**
 * Create a new influencer
 */
export async function addInfluencer(params: {
  firstName: string;
  lastName: string;
  nickname: string;
  location: {
    country: string;
    state: string;
  };
  contacts: {
    phoneNumbers: string[];
    socialMediaLinks: string[];
  };
}): Promise<InfluencerSchema> {
  const response: any = await apiClient.post('/referral/influencers', params);
  return response.data;
}

/**
 * Update an influencer
 */
export async function updateInfluencer(params: {
  influencer: InfluencerSchema;
  newData: Partial<InfluencerSchema>;
}): Promise<InfluencerSchema> {
  const response: any = await apiClient.put(`/referral/influencers/${params.influencer.influencerId}`, params);
  return response.data;
}

/**
 * Delete an influencer
 */
export async function deleteInfluencer(params: { influencer: InfluencerSchema }): Promise<void> {
  await apiClient.delete(`/referral/influencers/${params.influencer.influencerId}`, {
    data: params,
  });
}

/**
 * Check if an influencer can be deleted
 */
export async function canDeleteInfluencer(params: { influencer: InfluencerSchema }): Promise<boolean> {
  const response: any = await apiClient.get(`/referral/influencers/${params.influencer.influencerId}/can-delete`);
  return response.data;
}

/**
 * Pay an influencer
 */
export async function payInfluencer(params: {
  influencer: InfluencerSchema;
  paidAmountInDinars: number;
}): Promise<void> {
  await apiClient.post(`/referral/influencers/${params.influencer.influencerId}/pay`, params);
}

/**
 * Check if there are any referral requests
 */
export async function hasReferralRequests(): Promise<boolean> {
  const response: any = await apiClient.get('/referral/influencers/referral-requests/has');
  return response.data;
}

/**
 * Validate all referral requests
 */
export async function validateAllReferralRequests(): Promise<void> {
  await apiClient.post('/referral/influencers/referral-requests/validate-all');
}
